import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

// Components
import { StatusBadge, Table, Avatar, Button, Amount } from '@/components/ui';
import { AuthButton } from '@/core/permissions/AuthButton';

// Utils
import { formatDate } from '@/utils/format';
import { cn } from '@/utils/cn';

// Types
import type { MaintenanceContract } from '../types/contracts.types';

interface ContractsDesktopTableProps {
  contracts: MaintenanceContract[];
  onEdit: (contract: MaintenanceContract) => void;
  onDelete: (contract: MaintenanceContract) => void;
  isLoading?: boolean;
  type?: 'construction' | 'maintenance' | 'all';
}

/**
 * ContractsDesktopTable - Standardized Table for Maintenance Contracts
 * Uses Landscape V2 architectural patterns and smart UI components.
 */
export function ContractsDesktopTable({
  contracts,
  onEdit,
  onDelete,
  isLoading,
  type = 'maintenance'
}: ContractsDesktopTableProps) {
  const navigate = useNavigate();
  const basePath = type === 'construction' ? '/construction/contracts' : '/maintenance/contracts';

  const filteredContracts = useMemo(() => {
    if (type === 'all') return contracts;
    return contracts.filter(c => c.contract_type === type);
  }, [contracts, type]);

  const columns = useMemo(() => [
    {
      key: 'contract_code',
      title: 'Mã hợp đồng',
      className: 'w-[140px]',
      render: (code: string) => (
        <span className="font-mono text-[11px] font-bold text-os-primary tracking-wider ">
          {code}
        </span>
      )
    },
    {
      key: 'contract_name',
      title: 'Tên hợp đồng & Dự án',
      render: (_: string, item: MaintenanceContract) => (
        <div className="flex flex-col max-w-[300px]">
          <span className="font-bold text-slate-900 line-clamp-1" title={item.contract_name}>
            {item.contract_name}
          </span>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className="text-[10px] text-slate-500 font-bold truncate" title={item.contractor_name || ''}>
              {item.contractor_name || '---'}
            </span>
            <span className="text-slate-300">•</span>
            <span className="text-[10px] text-slate-400 font-medium truncate" title={item.project?.name || ''}>
              {item.project?.name}
            </span>
          </div>
        </div>
      )
    },
    {
      key: 'package_type',
      title: 'Gói thầu',
      className: 'w-[130px]',
      render: (pkg: string) => (
        <StatusBadge status={pkg} type="package" size="sm" />
      )
    },
    {
      key: 'contract_value',
      title: 'Giá trị',
      align: 'right' as const,
      className: 'w-[150px]',
      render: (val: number, item: MaintenanceContract) => (
        <div className="flex flex-col items-end">
          <Amount
            value={val}
            format="compact"
            suffix="VNĐ"
            className="text-sm font-bold text-slate-900"
          />
          {type === 'all' && (
            <StatusBadge
              status={item.contract_type}
              type="contract-type"
              size="sm"
              className="mt-1"
            />
          )}
        </div>
      )
    },
    {
      key: 'dates',
      title: 'Thời hạn',
      className: 'w-[150px]',
      render: (_: any, item: MaintenanceContract) => (
        <div className="flex flex-col">
          <span className="text-xs font-bold text-slate-700">
            {formatDate(item.start_date)}
          </span>
          <span className="text-[10px] text-slate-400 italic">
            đến {item.end_date ? formatDate(item.end_date) : '---'}
          </span>
        </div>
      )
    },
    {
      key: 'status_code',
      title: 'Trạng thái',
      align: 'center' as const,
      className: 'w-[130px]',
      render: (status: string) => (
        <StatusBadge status={status} type="contract" size="sm" />
      )
    },
    {
      key: 'actions',
      title: '',
      align: 'right' as const,
      className: 'w-[120px]',
      render: (_: any, item: MaintenanceContract) => (
        <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="sm"
            icon="ri-eye-line"
            onClick={() => navigate(`${basePath}/detail/${item.id}`)}
          />
          <AuthButton
            module="maintenance_contracts"
            action="update"
            variant="ghost"
            size="sm"
            icon="ri-edit-line"
            onClick={() => onEdit(item)}
          />
          <AuthButton
            module="maintenance_contracts"
            action="delete"
            variant="ghost"
            size="sm"
            icon="ri-delete-bin-line"
            className="text-red-500 hover:bg-red-50"
            onClick={() => onDelete(item)}
          />
        </div>
      )
    }
  ], [basePath, navigate, onEdit, onDelete, type]);

  return (
    <Table
      columns={columns}
      data={filteredContracts}
      isLoading={isLoading}
      onRowClick={(item) => navigate(`${basePath}/detail/${item.id}`)}
    />
  );
}
